<template>
    <div class="module">
        <section class="module__body">
            <div class="title-block">
                <div class="title-block__icon" />
                <div class="title-block__body">
                    <h1>
                        Assets
                        <span class="beta-tag">Beta</span>
                    </h1>
                    <span class="asset-count">
                        <span data-test-id="title-asset-count">{{ assetCount }}</span>
                        assets
                    </span>
                </div>
                <div class="title-block__button">
                    <create-asset-menu />
                </div>
            </div>

            <SelectButton
                v-model="currentViewId"
                :allowEmpty="false"
                :options="options"
                optionValue="id"
                optionLabel="label"
                fluid
            >
                <template #option="slotProps">
                    <span :data-test-id="`search-view-buton-${slotProps.option.id}`">{{ slotProps.option.label }}</span>
                </template>
            </SelectButton>
            <Search
                :columns="currentView.columns"
                :view="currentViewId"
                @page-loaded="searchPageLoaded"
            />
        </section>
    </div>
</template>

<script>
import moment from 'moment-timezone'
import SelectButton from 'primevue/selectbutton'
import Search from '@/components/Search/index.vue'
import CreateAssetMenu from '@/components/CreateAssetMenu.vue'
import { assetTypeOptions, assetTypeOptionsFor } from '@/constants/asset-types'
import { ASSET_RADIO, RELAY } from '@/constants/object-types'
import { yesNoOptions } from '@/constants/yes-no'

const timezoneString = moment().tz(moment.tz.guess()).format('z')

// Each view offers only the asset types it can return, so the options
// differ per view and the column cannot be a single shared definition.
const assetTypeColumn = (options) => ({
    dataType: 'enum',
    field: 'asset_type',
    filterable: true,
    label: 'Type',
    options,
    sortable: true,
})

const columnDefinitions = {
    assetNumber: {
        dataType: 'string',
        field: 'asset_number',
        filterable: true,
        label: 'Asset #',
        sortable: true,
    },
    createdAt: {
        dataType: 'datetime',
        field: 'created_at',
        filterable: true,
        label: 'Created',
        labelSuffix: `(${timezoneString})`,
        sortable: true,
    },
    description: {
        dataType: 'string',
        field: 'description',
        filterable: true,
        label: 'Description',
        sortable: true,
    },
    name: {
        dataType: 'string',
        field: 'name',
        filterable: true,
        label: 'Name',
        sortable: true,
    },
    radios: {
        dataType: 'link',
        field: 'radios',
        filterable: true,
        label: 'Radios',
        sortable: true,
    },
    assembly: {
        dataType: 'link',
        field: 'assembly',
        filterable: true,
        label: 'Assigned To',
        sortable: true,
    },
    serialNumber: {
        dataType: 'string',
        field: 'serial_number',
        filterable: true,
        label: 'Serial #',
        sortable: true,
    },
    smartEdgeEnabled: {
        dataType: 'enum',
        field: 'is_smart_device_enabled',
        filterable: true,
        label: 'SE Enabled',
        options: yesNoOptions,
        sortable: true,
    },
    updatedAt: {
        dataType: 'datetime',
        field: 'updated_at',
        filterable: true,
        label: 'Updated',
        labelSuffix: `(${timezoneString})`,
        sortable: true,
    },
    actions: {
        dataType: 'actions',
        field: 'actions',
        sortable: false,
    },
}

const views = [
    {
        id: 'assets',
        label: 'View All',
        columns: [
            assetTypeColumn(assetTypeOptions),
            columnDefinitions.name,
            columnDefinitions.serialNumber,
            columnDefinitions.assetNumber,
            columnDefinitions.createdAt,
            columnDefinitions.updatedAt,
            columnDefinitions.actions,
        ],
    },
    {
        id: 'radios',
        label: 'View Radios',
        columns: [
            assetTypeColumn(assetTypeOptionsFor([ASSET_RADIO])),
            columnDefinitions.serialNumber,
            columnDefinitions.assetNumber,
            columnDefinitions.description,
            columnDefinitions.assembly,
            columnDefinitions.createdAt,
            columnDefinitions.updatedAt,
            columnDefinitions.actions,
        ],
    },
    {
        id: 'relays',
        label: 'View Relays',
        columns: [
            assetTypeColumn(assetTypeOptionsFor([RELAY])),
            columnDefinitions.name,
            {
                dataType: 'datetime',
                field: 'latest_health_check_at',
                label: 'Last Response',
                labelSuffix: `(${timezoneString})`,
                filterable: true,
                sortable: true,
            },
            columnDefinitions.serialNumber,
            columnDefinitions.assetNumber,
            columnDefinitions.smartEdgeEnabled,
            columnDefinitions.description,
            columnDefinitions.radios,
            columnDefinitions.createdAt,
            columnDefinitions.updatedAt,
            columnDefinitions.actions,
        ],
    },
]

export default {
    components: {
        Search,
        SelectButton,
        CreateAssetMenu,
    },

    data() {
        const firstView = views[0]
        return {
            currentViewId: firstView.id,
            options: views,
            assetCount: 0,
        }
    },

    computed: {
        currentView() {
            return views.find(({ id }) => id === this.currentViewId)
        },
    },

    methods: {
        searchPageLoaded(count, totalCount) {
            this.assetCount = totalCount
        },
    },
}
</script>

<style lang="scss" scoped>
.module__body {
    .title-block {
        display: flex;
        width: 100%;
        margin-bottom: 20px;
        &__icon {
            width: 52px;
            height: 52px;
            background-color: #212628;
            background-image: url(@images/assets/assets-icon-white.svg);
            background-repeat: no-repeat;
            background-position: center center;
            background-size: 30px 30px;
            border-radius: 5px;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 36px;
            margin-right: 10px;
        }
        &__body {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-self: stretch;
            margin: 2px 0;
            h1 {
                font-size: 24px;
                display: flex;
                align-items: center;
            }
        }
        &__button {
            margin-left: auto;
        }
    }
}
</style>
